import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiClientError } from '@/lib/api-client';
import { invalidateInboundChainQueries } from '@/features/operations/inbound/useInbound';
import {
  applyCorrectionRequest,
  approveCorrectionRequest,
  createCorrectionRequest,
  fetchCorrectionRequests,
  fetchCorrectionSchema,
  fetchCorrectionTargets,
  rejectCorrectionRequest,
} from './api';
import type {
  CorrectionListQuery,
  CorrectionRequestDto,
  CorrectionTargetOption,
  CorrectionTypeSpec,
  CreateCorrectionRequest,
} from './types';

const KEY = 'correction-requests' as const;

/** The typed correctable-entity catalog (spec-13 §8.8): static per session. */
export const useCorrectionSchema = () => {
  return useQuery<CorrectionTypeSpec[], ApiClientError>({
    queryKey: [KEY, 'schema'],
    queryFn: fetchCorrectionSchema,
    staleTime: Infinity,
  });
};

/** Authorized, batch-scoped target options; disabled until both are chosen. */
export const useCorrectionTargets = (entity: string, monthlyBatchId: string) => {
  return useQuery<CorrectionTargetOption[], ApiClientError>({
    queryKey: [KEY, 'targets', entity, monthlyBatchId],
    queryFn: () => fetchCorrectionTargets(entity, monthlyBatchId),
    enabled: entity.trim() !== '' && monthlyBatchId.trim() !== '',
  });
};

/** List correction requests with an optional status / batch filter. */
export const useCorrectionRequests = (query: CorrectionListQuery = {}) => {
  return useQuery<CorrectionRequestDto[], ApiClientError>({
    queryKey: [KEY, 'list', query],
    queryFn: () => fetchCorrectionRequests(query),
    placeholderData: (prev) => prev,
  });
};

/** POST a new correction request. */
export const useCreateCorrectionRequest = () => {
  const qc = useQueryClient();
  return useMutation<CorrectionRequestDto, ApiClientError, CreateCorrectionRequest>({
    mutationFn: (body) => createCorrectionRequest(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY, 'list'] }),
  });
};

/** Shared shape for the three id-scoped transitions (approve/reject/apply). */
const useTransition = (fn: (id: string) => Promise<CorrectionRequestDto>) => {
  const qc = useQueryClient();
  return useMutation<CorrectionRequestDto, ApiClientError, string>({
    mutationFn: (id) => fn(id),
    onSuccess: (request) => {
      if (request.status === 'applied') invalidateInboundChainQueries(qc);
      return qc.invalidateQueries({ queryKey: [KEY, 'list'] });
    },
  });
};

export const useApproveCorrectionRequest = () => useTransition(approveCorrectionRequest);
export const useRejectCorrectionRequest = () => useTransition(rejectCorrectionRequest);
export const useApplyCorrectionRequest = () => useTransition(applyCorrectionRequest);
